import {
  type FC,
  type PropsWithChildren,
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { COURSE_STATE } from '@~/constants/student-courses';
import { useAppSelector } from '@~/hooks';
import { type TAssignedCourseData } from '@~/modules/enter-class-modal-v2/types';
import { getStudentCourses } from '@~/reducers/home-details';
import { type TCourseStream } from '@~/types/course';

interface ICourseSwitchContextProps {
  courseValue?: TCourseStream;
  onCourseValueUpdate: (updatedCourseValue: TCourseStream) => void;
}

export const CourseSwitchContext = createContext<ICourseSwitchContextProps>({
  courseValue: undefined,
  onCourseValueUpdate: () => null,
});

type TCourseValueLS = Record<string, TCourseStream | undefined>;

const CourseSwitcherProvider: FC<PropsWithChildren> = memo(
  function CourseSwitcherProvider({ children }) {
    const storedCourseValue = useMemo(
      () =>
        JSON.parse(
          localStorage.getItem('current_course_stream') ?? `null`,
        ) as TCourseValueLS,
      [],
    );
    const status = useAppSelector(state => state.status);
    const userId = useAppSelector(storeState => storeState.auth.userId) ?? '';
    const homeDetails = useAppSelector(storeState => storeState.homeDetails);
    const { course_stream: courseStream, ongoing, class_type } = status || {};
    const assignedCourses: TAssignedCourseData = useMemo(
      () => getStudentCourses(homeDetails, userId) || [],
      [homeDetails, userId],
    );

    const storedCourseValueIsActive = useMemo(
      () =>
        assignedCourses.find(
          ({ course_name: courseName }) =>
            courseName.toUpperCase() === storedCourseValue?.[userId],
        ),
      [assignedCourses, storedCourseValue, userId],
    );

    const studentHaveActiveMath = useMemo(
      () =>
        assignedCourses.some(
          course =>
            course.state === COURSE_STATE.ACT &&
            course.course_name.toUpperCase() === 'MATH',
        ),
      [assignedCourses],
    );

    const studentActiveCourse = useMemo(
      () => assignedCourses.find(course => course.state === COURSE_STATE.ACT),
      [assignedCourses],
    );

    const studentActiveCourseButPaused = useMemo(
      () =>
        assignedCourses.find(course =>
          [COURSE_STATE.PSD, COURSE_STATE.READMIT, COURSE_STATE.SUSPENDED].includes(
            course.state,
          ),
        ),
      [assignedCourses],
    );

    const defaultCourseValue = useMemo<TCourseStream | undefined>(() => {
      if (ongoing) {
        if (class_type === 'DEMO') return undefined;

        if (courseStream) return courseStream;
      }

      if (storedCourseValue?.[userId] && storedCourseValueIsActive)
        return storedCourseValue[userId];

      if (studentHaveActiveMath) return 'MATH';

      if (studentActiveCourse?.course_name)
        return studentActiveCourse.course_name.toUpperCase() as TCourseStream;

      if (!studentActiveCourse && studentActiveCourseButPaused)
        return studentActiveCourseButPaused.course_name.toUpperCase() as TCourseStream;
    }, [
      ongoing,
      storedCourseValue,
      userId,
      storedCourseValueIsActive,
      studentHaveActiveMath,
      studentActiveCourse,
      class_type,
      courseStream,
      studentActiveCourseButPaused,
    ]);

    const [courseValue, setCourseValue] = useState<TCourseStream | undefined>(
      defaultCourseValue,
    );

    const handleOnCourseValueUpdate = useCallback(
      (updatedCourseValue?: TCourseStream) => {
        const payload = {
          ...storedCourseValue,
          [userId]: updatedCourseValue,
        };

        localStorage.setItem('current_course_stream', JSON.stringify(payload));

        setCourseValue(updatedCourseValue);
      },
      [storedCourseValue, userId],
    );

    const contextValue = useMemo(
      () => ({ courseValue, onCourseValueUpdate: handleOnCourseValueUpdate }),
      [courseValue, handleOnCourseValueUpdate],
    );

    useEffect(() => {
      handleOnCourseValueUpdate(defaultCourseValue);
    }, [defaultCourseValue, handleOnCourseValueUpdate]);

    return (
      <CourseSwitchContext.Provider value={contextValue}>
        {children}
      </CourseSwitchContext.Provider>
    );
  },
);

export default CourseSwitcherProvider;
